/**
 * Configuration Loader Module
 * Loads region and spots configuration from JSON files
 */

import { logger } from './logger.js';
import { normalizeSpot } from './spot-normalize.js';
import { DEFAULTS, DEFAULT_COORDS, DEFAULT_WEATHER_POINT, DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from '../shared/defaults.mjs';

// Re-export for consumers that historically imported from config-loader
export { normalizeSpot };

let regionsCache = null;
let spotsCache = null;

/**
 * Load region configuration from the data file
 * Uses caching to avoid repeated fetches
 * @returns {Promise<Object>} The full regions configuration
 */
export async function loadRegions() {
    if (regionsCache) {
        return regionsCache;
    }

    try {
        const response = await fetch('/data/regions.json', {
            cache: 'default'
        });

        if (!response.ok) {
            throw new Error(`Failed to load regions: ${response.status}`);
        }

        regionsCache = await response.json();
        return regionsCache;
    } catch (error) {
        logger.error('Error loading regions configuration:', error);
        // Return a minimal fallback config for backwards compatibility
        return getFallbackConfig();
    }
}

/**
 * Get a specific region by ID
 * @param {string} regionId - Region ID
 * @returns {Promise<Object|null>} Region object or null
 */
export async function getRegion(regionId) {
    const config = await loadRegions();
    return config.regions[regionId] || null;
}

/**
 * Get all available region IDs and names
 * Useful for populating region selector dropdowns
 * @returns {Promise<Array>} Array of {id, name, shortName} objects
 */
export async function getRegionList() {
    const config = await loadRegions();
    return Object.entries(config.regions).map(([id, region]) => ({
        id,
        name: region.name,
        shortName: region.shortName
    }));
}

/**
 * Get all stations across all regions and areas
 * Useful for station search functionality
 * @returns {Promise<Array>} Array of station objects with regionId and areaId
 */
export async function getAllStations() {
    const config = await loadRegions();
    const stations = [];

    for (const [regionId, region] of Object.entries(config.regions)) {
        // Handle v2 format with areas
        if (region.areas) {
            for (const [areaId, area] of Object.entries(region.areas)) {
                for (const [stationId, station] of Object.entries(area.stations || {})) {
                    stations.push({
                        ...station,
                        id: stationId,
                        regionId,
                        regionName: region.name,
                        areaId,
                        areaName: area.name
                    });
                }
            }
        } else {
            // Handle v1 format (backwards compatibility)
            for (const [stationId, station] of Object.entries(region.stations || {})) {
                stations.push({
                    ...station,
                    id: stationId,
                    regionId,
                    regionName: region.name,
                    areaId: null,
                    areaName: null
                });
            }
        }
    }

    return stations;
}

/**
 * Get the default region ID from configuration
 * @returns {Promise<string>} Default region ID
 */
export async function getDefaultRegion() {
    const config = await loadRegions();
    return config.defaultRegion || DEFAULTS.region;
}

/**
 * Clear the cached configuration
 * Useful for testing or forcing a refresh
 */
export function clearCache() {
    regionsCache = null;
    spotsCache = null;
}

/**
 * Load spots configuration from the data file
 * Uses caching to avoid repeated fetches.
 * All spots are normalized (camelCase mirrors added, snake_case kept) so
 * every downstream consumer sees the same shape as the backend transformSpot.
 * @returns {Promise<Object>} The spots data with spots array
 */
export async function loadSpots() {
    if (spotsCache) {
        return spotsCache;
    }

    try {
        const response = await fetch('/data/spots.json', {
            cache: 'default'
        });

        if (!response.ok) {
            throw new Error(`Failed to load spots: ${response.status}`);
        }

        const raw = await response.json();
        spotsCache = {
            ...raw,
            spots: (raw.spots || []).map(normalizeSpot)
        };
        logger.log(`Loaded ${spotsCache.spots?.length || 0} spots from spots.json`);
        return spotsCache;
    } catch (error) {
        logger.error('Error loading spots configuration:', error);
        // Return empty spots array as fallback
        return { version: '1.0', spots: [] };
    }
}

/**
 * Find spot configuration by NOAA station ID
 * Returns the first matching spot (there may be multiple spots per station)
 * @param {string} stationId - NOAA station ID
 * @returns {Promise<Object|null>} Spot config or null
 */
export async function getSpotByStationId(stationId) {
    const spotsData = await loadSpots();
    if (!spotsData.spots) return null;

    // Find first spot matching this station
    return spotsData.spots.find(spot => spot.noaa_station_id === stationId) || null;
}

/**
 * Find all spots for a NOAA station ID
 * @param {string} stationId - NOAA station ID
 * @returns {Promise<Array>} Array of spot configs
 */
export async function getSpotsByStationId(stationId) {
    const spotsData = await loadSpots();
    if (!spotsData.spots) return [];

    return spotsData.spots.filter(spot => spot.noaa_station_id === stationId);
}

/**
 * Find spot configuration by slug
 * @param {string} slug - Spot slug (e.g., 'blacks-beach')
 * @returns {Promise<Object|null>} Spot config or null
 */
export async function getSpotBySlug(slug) {
    const spotsData = await loadSpots();
    if (!spotsData.spots) return null;

    return spotsData.spots.find(spot => spot.slug === slug) || null;
}

/**
 * Fallback configuration for backwards compatibility
 * Used when regions.json fails to load
 * Uses v2 format with areas
 */
function getFallbackConfig() {
    return {
        version: '2.0',
        defaultRegion: DEFAULTS.region,
        regions: {
            [DEFAULTS.region]: {
                id: DEFAULTS.region,
                name: 'California',
                shortName: 'CA',
                timezone: 'America/Los_Angeles',
                defaultArea: DEFAULTS.area,
                areas: {
                    [DEFAULTS.area]: {
                        id: DEFAULTS.area,
                        name: 'San Diego County',
                        shortName: 'San Diego',
                        mapCenter: DEFAULT_MAP_CENTER,
                        mapZoom: DEFAULT_MAP_ZOOM,
                        buoys: {
                            primary: '46232',
                            fallback: []
                        },
                        defaultStation: DEFAULTS.station,
                        stations: {
                            [DEFAULTS.station]: {
                                name: 'San Diego',
                                lat: DEFAULT_COORDS.lat,
                                lon: DEFAULT_COORDS.lon,
                                weatherPoint: DEFAULT_WEATHER_POINT,
                                buoyOverride: null,
                                localName: 'Downtown San Diego'
                            },
                            '9410230': {
                                name: 'La Jolla',
                                lat: 32.8669,
                                lon: -117.2571,
                                weatherPoint: { lat: 32.8669, lon: -117.2571 },
                                buoyOverride: null,
                                localName: 'La Jolla Shores'
                            }
                        }
                    }
                }
            }
        },
        _legacyMapping: {
            socal: { region: DEFAULTS.region, area: DEFAULTS.area }
        }
    };
}
